import {getStoredTheme, getThemeToApply, setStoredTheme} from "../util/themeUtil";
import type {RevaThunkAction} from "./revaHooks";
import {
    type AppliedTheme,
    selectAppliedTheme,
    selectCurrentTheme,
    THEME_AUTO,
    THEME_DARK,
    THEME_LIGHT,
    THEME_LINK_ID,
    THEMES,
    type Theme,
    updateTheme,
} from "./themeSlice";

const VALID_THEMES: Theme[] = [THEME_LIGHT, THEME_DARK, THEME_AUTO];

const changePrimeReactTheme = (appliedTheme: AppliedTheme, themeToApply: AppliedTheme): void => {
    const linkElement = document.getElementById(THEME_LINK_ID);
    if (!linkElement) {
        return;
    }
    const href = linkElement.getAttribute("href");
    if (!href) {
        return;
    }
    const cloneLinkElement = linkElement.cloneNode(true) as HTMLLinkElement;
    const newThemeUrl = href.replace(appliedTheme, themeToApply);
    cloneLinkElement.setAttribute("id", `${THEME_LINK_ID}-clone`);
    cloneLinkElement.setAttribute("href", newThemeUrl);
    cloneLinkElement.addEventListener("load", () => {
        linkElement.remove();
        cloneLinkElement.setAttribute("id", THEME_LINK_ID);
    });
    const linkElementParentNode = linkElement.parentNode;
    if (linkElementParentNode) {
        linkElementParentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);
    }
};

const changeDeclaredThemeClass = (themeToApply: AppliedTheme): void => {
    const bodyElement = document.body;
    if (themeToApply === THEMES[THEME_DARK]) {
        bodyElement.classList.add("app-theme-dark");
        bodyElement.classList.remove("app-theme-light");
    } else {
        bodyElement.classList.add("app-theme-light");
        bodyElement.classList.remove("app-theme-dark");
    }
};

/**
 * Thunk to change the theme, depending on current theme.
 * @param newTheme new Theme ID (THEME_LIGHT, THEME_DARK or THEME_AUTO)
 */
export const setTheme =
    (newTheme: Theme): RevaThunkAction<void> =>
    (dispatch, getState, {env}) => {
        if (!VALID_THEMES.includes(newTheme)) {
            console.error(`Called setTheme() with invalid theme ${newTheme}`);
            return;
        }
        const state = getState();
        const currentTheme = selectCurrentTheme(state);
        const appliedTheme = selectAppliedTheme(state);
        const themeToApply = getThemeToApply(newTheme);

        changeDeclaredThemeClass(themeToApply);

        if (currentTheme === newTheme) {
            if (env.isDevelopmentMode) {
                console.debug(`New theme ${newTheme} is identical to current theme.`);
            }
            return;
        }
        if (env.isDevelopmentMode) {
            console.log(`Setting theme to ${newTheme}`);
        }
        setStoredTheme(newTheme);
        if (themeToApply !== appliedTheme) {
            changePrimeReactTheme(appliedTheme, themeToApply);
        }
        dispatch(updateTheme({theme: newTheme, appliedTheme: themeToApply}));
    };

export const initializeTheme = (): RevaThunkAction<void> => (dispatch) => {
    dispatch(setTheme(getStoredTheme()));
};
